import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { IModules, IStation } from "../../../../models/IUser";
import { DeviceTypeConfig, isStationVisible } from "../deviceTypeConfig";
import {
  updateItemWithModified,
  resetModified,
  filterModified,
  WithModified,
  deepClone,
} from "../utils/modifiedFields";

/**
 * `К2.6.1`: видимые строки таблицы станций считаются при показе по текущей
 * модели шкафа — в том числе по несохранённой (`editedModule`). Сам
 * `stationsState` от модели не зависит, поэтому переключение 9/15 не теряет
 * несохранённые правки станций.
 */
export function visibleStations(
  rows: WithModified<IStation>[],
  cabinetModel: unknown,
): WithModified<IStation>[] {
  return rows.filter((row) =>
    isStationVisible(cabinetModel, row.controller_address),
  );
}

export function useStationsState(
  module: IModules | null,
  stations: IStation[],
  config: DeviceTypeConfig,
  markMutated: () => void,
) {
  const [stationsState, setStationsState] = useState<WithModified<IStation>[]>(
    [],
  );

  // `G.2` + `К2.6.1`: в состоянии — все станции модуля, без отбора по модели.
  // Модель шкафа может быть ещё не сохранена (`editedModule`), и если собирать
  // `stationsState` по `module.cabinet_model`, то при переключении 9/15 таблица
  // не перерисуется до «Сохранить». Видимость строк считается при показе —
  // см. `visibleStations`.
  const stationsToDisplay =
    module && config.tableMode === "stations"
      ? stations.filter((el) => el.module_id === module.id)
      : [];

  useEffect(() => {
    setStationsState(
      stationsToDisplay.map((station) => ({
        ...deepClone(station),
        __modified: [],
      })),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stations, module?.id]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    stationId?: number,
  ) => {
    if (stationId === undefined) return;

    markMutated();
    const { name, value } = e.target;

    if (name === "controller_address") {
      const num = parseInt(value, 10);
      if (isNaN(num) || num < 1 || num > 15) {
        toast.error("Значение адреса контроллера должно быть от 1 до 15");
      }
    }

    setStationsState((prevList) =>
      prevList.map((station) => {
        if (station.id !== stationId) return station;

        const updated = updateItemWithModified(station, name, value);

        // `31.1`: человек поправил место — прежний отказ по нему больше не
        // показываем: он относился к прежнему вводу.
        if (name === "place") {
          return { ...updated, __placeError: undefined };
        }

        // `DEV-91.3`: отказ по счётчику — тоже у своей строки.
        if (
          name === "Meters.meter_address" ||
          name === "Meters.meter_number"
        ) {
          return { ...updated, __meterError: undefined };
        }

        return updated;
      }),
    );
  };

  /**
   * `31.1` (`РГ-07`): отказ по колонке «Место установки» — на строку, у которой
   * правили место. Причина ставится **на строку**, а не общим тостом: строк в
   * таблице много, и человек должен видеть, к какой из них относится отказ.
   */
  const applyPlaceError = (message: string | null) => {
    if (!message) return;

    setStationsState((prevList) =>
      prevList.map((station) =>
        station.__modified?.includes("place")
          ? { ...station, __placeError: message }
          : station,
      ),
    );
  };

  /**
   * `DEV-91.4` (`АУД-Н264`), `К2.2` (`АУД-Н268`): отказ по счётчику — только у
   * строки станции, которую назвал back. `station` — адрес контроллера из
   * ответа; `null` — сервер станцию не назвал, тогда причина остаётся у всех
   * правленых строк. С `id` строки таблицы адрес не сверяем: id и адрес — разные
   * числа, и совпадение привязывало отказ к чужой строке.
   */
  const applyMeterError = (message: string | null, station: string | null = null) => {
    if (!message) return;

    setStationsState((prevList) =>
      prevList.map((row) => {
        const touched =
          row.__modified?.includes("Meters.meter_address") ||
          row.__modified?.includes("Meters.meter_number");

        if (!touched) return row;

        if (
          station !== null &&
          String(row.controller_address) !== String(station)
        ) {
          return row;
        }

        return { ...row, __meterError: message };
      }),
    );
  };

  const reset = () => {
    setStationsState((prev) => prev.map(resetModified));
  };

  return {
    stationsState,
    handleChange,
    applyPlaceError,
    applyMeterError,
    reset,
    // `31.1`: какие строки ждут разбора после отказа по месту — видно и снаружи.
    placeErrors: stationsState
      .filter((station) => Boolean(station.__placeError))
      .map((station) => ({ stationId: station.id, message: station.__placeError })),
    // `DEV-91.4`: отказы по счётчику — видно и снаружи (по строке станции).
    meterErrors: stationsState
      .filter((station) => Boolean(station.__meterError))
      .map((station) => ({ stationId: station.id, message: station.__meterError })),
    modifiedStations: filterModified(stationsState),
  };
}
